import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { CountryFlag, CountryOption, getAllCountries, getCountryByCode } from './CountryPicker';

interface MultiCountryPickerProps {
  value: string[];
  onChange: (codes: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  /** Maximum tags to display before showing "+N more" */
  maxDisplayTags?: number;
  className?: string;
}

const countryOptions = getAllCountries();

export function MultiCountryPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  maxDisplayTags = 3,
  className,
}: MultiCountryPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const selectedOptions = useMemo(
    () =>
      value
        .map((code) => getCountryByCode(code))
        .filter((c): c is CountryOption => c !== undefined),
    [value]
  );

  const handleSelect = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const handleRemove = (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((c) => c !== code));
  };

  const displayedOptions = selectedOptions.slice(0, maxDisplayTags);
  const remainingCount = selectedOptions.length - maxDisplayTags;

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'justify-between font-normal h-auto min-h-10',
              fullWidth && 'w-full',
              size === 'sm' && 'min-h-8 text-sm',
              selectedOptions.length === 0 && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            <div className="flex flex-wrap gap-1 py-1">
              {selectedOptions.length === 0 ? (
                placeholder ?? t('pickers.selectCountries')
              ) : (
                <>
                  {displayedOptions.map((option) => (
                    <Badge
                      key={option.code}
                      variant="secondary"
                      className="gap-1 pe-1"
                    >
                      <CountryFlag code={option.code} size={12} />
                      <span>{option.name}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        className="ms-1 rounded-full p-0.5 hover:bg-muted cursor-pointer"
                        onClick={(e) => handleRemove(option.code, e)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRemove(option.code, e as unknown as React.MouseEvent); }}
                      >
                        <X className="h-3 w-3" />
                      </span>
                    </Badge>
                  ))}
                  {remainingCount > 0 && (
                    <Badge variant="outline" className="text-muted-foreground">
                      {t('pickers.moreCountries', { count: remainingCount })}
                    </Badge>
                  )}
                </>
              )}
            </div>
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] max-w-[calc(100vw-2rem)] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchCountry')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noCountryFound')}</CommandEmpty>
              <CommandGroup>
                {countryOptions.map((option) => (
                  <CommandItem
                    key={option.code}
                    value={`${option.name} ${option.code}`}
                    onSelect={() => handleSelect(option.code)}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4',
                        value.includes(option.code) ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <CountryFlag code={option.code} size={16} />
                    <span className="ms-2 flex-1">{option.name}</span>
                    <span className="text-muted-foreground text-xs">{option.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}
